/**
 * =============================================================================
 * IMAGE UPLOADER COMPONENT
 * =============================================================================
 *
 * A drag-and-drop image uploader for the storefront widget.
 * Allows customers to upload up to 5 images when submitting questions.
 *
 * ARCHITECTURE REFERENCE: PHASE_0B_SPEC.md §2.5.3
 * PATTERN REFERENCE: PHASE_0B_PATTERNS.md §7
 *
 * Features:
 * - Drag-and-drop zone with visual feedback
 * - Click to open file browser
 * - Image thumbnail previews
 * - Client-side validation (file type, size, count)
 * - Remove button for each image
 * - Accessible error display
 *
 * =============================================================================
 */

import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { FILE_VALIDATION } from '@find-my-part/types';
import { useStyleInjection } from '../hooks';
import { BREAKPOINTS } from '../tokens';

// =============================================================================
// TYPES
// =============================================================================

export interface ImageUploaderProps {
  /** Currently selected images */
  images: File[];
  /** Callback when images change (add/remove) */
  onImagesChange: (files: File[]) => void;
  /** Whether the component is disabled (e.g., during form submission) */
  disabled?: boolean;
  /**
   * Client-side selection cap (UX only — the server chokepoint is the
   * authoritative gate). Defaults to the per-question ceiling so existing
   * question-path callers are unchanged; the reply path passes
   * FILE_VALIDATION.MAX_IMAGES_PER_REPLY (ADR-022 §12 PR5).
   */
  maxImages?: number;
  /**
   * Exposes Ask's camera and ordinary-picker controls. Both controls feed the
   * same validation and upload state; ReplyForm leaves this disabled.
   */
  enableCameraCapture?: boolean;
  /** Additional CSS class name */
  className?: string;
}

export interface ValidationError {
  type: 'file_type' | 'file_size' | 'max_count';
  message: string;
  fileName?: string;
}

const ACCEPTED_IMAGE_TYPES = [
  ...FILE_VALIDATION.ALLOWED_MIME_TYPES,
  ...FILE_VALIDATION.ALLOWED_EXTENSIONS.map((ext) => `.${ext}`),
].join(',');

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Validate a file against type and size constraints.
 *
 * @param file - File to validate
 * @returns Array of validation errors (empty if valid)
 */
function validateFile(file: File): ValidationError[] {
  const errors: ValidationError[] = [];

  // Check file type. Client validation is UX-only — the Worker magic-byte
  // validates every upload — so it must be PERMISSIVE, not stricter than the
  // server: some browsers (notably iOS Safari) hand a HEIC/HEIF file an EMPTY
  // MIME type (`file.type === ''`), which the server would still accept. Fall
  // back to the filename extension in exactly that empty-MIME case (the guard is
  // `!file.type` — empty only) so a valid photo isn't rejected before it can even
  // be uploaded. (A non-empty but non-image MIME is treated as the browser's
  // considered type and left to the normal allow-list check; the server remains
  // the authoritative gate regardless.)
  const isAllowedType = FILE_VALIDATION.ALLOWED_MIME_TYPES.includes(
    file.type as (typeof FILE_VALIDATION.ALLOWED_MIME_TYPES)[number]
  );
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const isAllowedByExtWhenMimeUnknown =
    !file.type &&
    FILE_VALIDATION.ALLOWED_EXTENSIONS.includes(
      ext as (typeof FILE_VALIDATION.ALLOWED_EXTENSIONS)[number]
    );
  if (!isAllowedType && !isAllowedByExtWhenMimeUnknown) {
    errors.push({
      type: 'file_type',
      message: `"${file.name}" is not a valid image type. Allowed: JPG, PNG, WebP, HEIC, HEIF.`,
      fileName: file.name,
    });
  }

  // Check file size
  if (file.size > FILE_VALIDATION.MAX_SIZE_BYTES) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    errors.push({
      type: 'file_size',
      message: `"${file.name}" is too large (${sizeMb}MB). Maximum size is ${FILE_VALIDATION.MAX_SIZE_MB}MB.`,
      fileName: file.name,
    });
  }

  return errors;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Image uploader component with drag-and-drop support.
 *
 * @example
 * ```tsx
 * const [images, setImages] = useState<File[]>([]);
 *
 * <ImageUploader
 *   images={images}
 *   onImagesChange={setImages}
 * />
 * ```
 */
export function ImageUploader({
  images,
  onImagesChange,
  disabled = false,
  maxImages = FILE_VALIDATION.MAX_IMAGES_PER_QUESTION,
  enableCameraCapture = false,
  className = '',
}: ImageUploaderProps): React.ReactElement {
  const [isDragActive, setIsDragActive] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [previews, setPreviews] = useState<Map<File, string>>(new Map());
  const pickerInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const pickerButtonRef = useRef<HTMLButtonElement>(null);
  const cameraButtonRef = useRef<HTMLButtonElement>(null);
  const uploadActionsRef = useRef<HTMLDivElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const previewRemoveRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const pendingRemovalFocusIndexRef = useRef<number | null>(null);
  const pendingErrorDismissFocusRef = useRef(false);
  const lastFileSourceRef = useRef<'camera' | 'picker' | 'dropzone'>('dropzone');
  // Ref to track current previews for reliable cleanup on unmount
  const previewsRef = useRef<Map<File, string>>(new Map());

  // Inject styles once per unique ID, shared across all ImageUploader instances
  useStyleInjection(imageUploaderStyles, 'fmp-image-uploader-styles');

  // Manage preview URLs: create for new files, revoke ONLY the URLs of files that
  // were removed. A surviving file keeps the SAME object URL across renders.
  //
  // The revoke MUST NOT run as this effect's cleanup: React runs a dependency
  // effect's cleanup before the next run, so revoking every current URL on each
  // `images` change would revoke the URLs the *next* run then reuses for
  // surviving files — a removed-non-last image would show a broken thumbnail
  // (decode-vs-revoke race). Revoking only removed files here + revoking the
  // remainder on true unmount (the separate effect below) avoids that.
  useEffect(() => {
    const newPreviews = new Map<File, string>();

    images.forEach((file) => {
      // Reuse existing preview URL if available
      const existingUrl = previewsRef.current.get(file);
      if (existingUrl) {
        newPreviews.set(file, existingUrl);
      } else {
        // Create new object URL for preview
        newPreviews.set(file, URL.createObjectURL(file));
      }
    });

    // Revoke URLs for files that were removed
    previewsRef.current.forEach((url, file) => {
      if (!newPreviews.has(file)) {
        URL.revokeObjectURL(url);
      }
    });

    // Update both ref and state
    previewsRef.current = newPreviews;
    setPreviews(newPreviews);
  }, [images]);

  // Revoke every remaining preview URL ONLY on true unmount (reads the ref at
  // teardown time so it covers whatever files are still selected).
  useEffect(
    () => () => {
      previewsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    },
    []
  );

  /**
   * Handle file selection from input or drop.
   *
   * Implements partial batch acceptance: if more files are dropped than remaining
   * slots, accepts as many as possible and shows an informative message about
   * how many were added vs rejected.
   *
   * FILE SELECTION STRATEGY: First-N files are accepted when over limit.
   * This matches OS file picker order and provides predictable behavior.
   *
   * VALIDATION ORDER:
   * 1. Count limit - Files are truncated to available slots before validation
   * 2. File type - MIME type must be in allowed list
   * 3. File size - Must be under MAX_SIZE_BYTES
   *
   * @param fileList - Files from input change or drop event
   */
  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0 || disabled) {
        return;
      }

      const allNewFiles = Array.from(fileList);
      const validationErrors: ValidationError[] = [];

      // Calculate remaining slots and limit files to what can fit (first-N strategy)
      const remainingSlots = maxImages - images.length;
      const filesToConsider = allNewFiles.slice(0, remainingSlots);
      const rejectedDueToCount = allNewFiles.length - filesToConsider.length;

      // Validate each file that fits within the slot limit
      const validFiles: File[] = [];
      for (const file of filesToConsider) {
        const fileErrors = validateFile(file);
        if (fileErrors.length > 0) {
          validationErrors.push(...fileErrors);
        } else {
          validFiles.push(file);
        }
      }

      // Show message if some files were rejected due to count limit
      // Note: Message uses validFiles.length (actual count added) not filesToConsider.length
      // The branch for validFiles.length === 0 is defensive code; normally unreachable
      // because the dropzone is disabled when at the image limit
      if (rejectedDueToCount > 0) {
        validationErrors.push({
          type: 'max_count',
          message:
            validFiles.length === 0
              ? `Cannot add any images. Maximum ${maxImages} images allowed.`
              : `Only ${validFiles.length} of ${allNewFiles.length} images added. Maximum ${maxImages} images allowed.`,
        });
      }

      // Update errors state
      setErrors(validationErrors);

      // Add valid files if any
      if (validFiles.length > 0) {
        onImagesChange([...images, ...validFiles]);
      }
    },
    [images, onImagesChange, disabled, maxImages]
  );

  /**
   * Handle file input change.
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      // Reset input value so same file can be selected again
      e.target.value = '';
    },
    [handleFiles]
  );

  /**
   * Handle click on drop zone.
   */
  const openPicker = useCallback(
    (returnFocusTo: 'picker' | 'dropzone') => {
      const atLimit = images.length >= maxImages;
      if (!disabled && !atLimit && pickerInputRef.current) {
        lastFileSourceRef.current = returnFocusTo;
        pickerInputRef.current.click();
      }
    },
    [disabled, images.length, maxImages]
  );

  const handlePickerButtonClick = useCallback(() => {
    openPicker('picker');
  }, [openPicker]);

  const handleDropzoneClick = useCallback(() => {
    openPicker('dropzone');
  }, [openPicker]);

  const handleCameraClick = useCallback(() => {
    const atLimit = images.length >= maxImages;
    if (!disabled && !atLimit) {
      lastFileSourceRef.current = 'camera';
      cameraInputRef.current?.click();
    }
  }, [disabled, images.length, maxImages]);

  /**
   * Handle keyboard activation.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const atLimit = images.length >= maxImages;
      if ((e.key === 'Enter' || e.key === ' ') && !disabled && !atLimit) {
        e.preventDefault();
        lastFileSourceRef.current = 'dropzone';
        pickerInputRef.current?.click();
      }
    },
    [disabled, images.length, maxImages]
  );

  /**
   * Handle drag enter.
   */
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragActive(true);
      }
    },
    [disabled]
  );

  /**
   * Handle drag leave.
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only deactivate if leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsDragActive(false);
  }, []);

  /**
   * Handle drag over.
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        e.dataTransfer.dropEffect = 'copy';
      }
    },
    [disabled]
  );

  /**
   * Handle drop.
   */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const atLimit = images.length >= maxImages;
      if (!disabled && !atLimit) {
        lastFileSourceRef.current = 'dropzone';
        handleFiles(e.dataTransfer.files);
      }
    },
    [disabled, images.length, handleFiles, maxImages]
  );

  /**
   * Remove an image from the list.
   */
  const handleRemove = useCallback(
    (fileToRemove: File, index: number) => {
      // Slice 2A owns managed successor focus for Ask's capture variant. Keep
      // the shared/default uploader behavior stable for ReplyForm.
      pendingRemovalFocusIndexRef.current = enableCameraCapture ? index : null;
      const newImages = images.filter((f) => f !== fileToRemove);
      onImagesChange(newImages);
      // Clear all errors - they may no longer be valid after removal
      // (e.g., max_count error should clear when under limit again)
      setErrors([]);
    },
    [enableCameraCapture, images, onImagesChange]
  );

  /**
   * Clear all errors.
   */
  const handleDismissErrors = useCallback(() => {
    pendingErrorDismissFocusRef.current = enableCameraCapture;
    setErrors([]);
  }, [enableCameraCapture]);

  const focusFileSourceOrPreview = useCallback(
    (source: 'camera' | 'picker' | 'dropzone') => {
      const preferredTarget =
        source === 'camera'
          ? cameraButtonRef.current
          : source === 'picker'
            ? pickerButtonRef.current
            : dropzoneRef.current;

      const preferredIsDisabled =
        preferredTarget instanceof HTMLButtonElement
          ? preferredTarget.disabled
          : preferredTarget?.getAttribute('aria-disabled') === 'true';
      if (preferredTarget && !preferredIsDisabled) {
        preferredTarget.focus();
        if (document.activeElement === preferredTarget) return;
      }

      // A count-limit error can disable the originating source control. In that
      // case the last remaining preview is the nearest useful enabled action.
      for (let index = images.length - 1; index >= 0; index -= 1) {
        const removeButton = previewRemoveRefs.current[index];
        if (removeButton && !removeButton.disabled) {
          removeButton.focus();
          return;
        }
      }

      // During submission every source can be disabled while an earlier
      // validation alert is still dismissible. Keep focus inside the uploader
      // on a visible semantic group instead of letting it fall to <body>.
      if (enableCameraCapture) uploadActionsRef.current?.focus();
      else dropzoneRef.current?.focus();
    },
    [enableCameraCapture, images.length]
  );

  // Restore focus during the same commit that removes a focused preview or
  // error-dismiss button. This avoids falling back to <body> and restarting the
  // keyboard path at the top of the host page.
  useLayoutEffect(() => {
    if (!enableCameraCapture) {
      pendingRemovalFocusIndexRef.current = null;
      return;
    }
    const requestedIndex = pendingRemovalFocusIndexRef.current;
    if (requestedIndex === null) return;

    pendingRemovalFocusIndexRef.current = null;
    if (images.length > 0) {
      const successorIndex = Math.min(requestedIndex, images.length - 1);
      previewRemoveRefs.current[successorIndex]?.focus();
      return;
    }

    focusFileSourceOrPreview(enableCameraCapture ? 'picker' : 'dropzone');
  }, [enableCameraCapture, focusFileSourceOrPreview, images]);

  useLayoutEffect(() => {
    if (!enableCameraCapture) {
      pendingErrorDismissFocusRef.current = false;
      return;
    }
    if (!pendingErrorDismissFocusRef.current || errors.length > 0) return;
    pendingErrorDismissFocusRef.current = false;

    focusFileSourceOrPreview(lastFileSourceRef.current);
  }, [enableCameraCapture, errors.length, focusFileSourceOrPreview]);

  const isAtLimit = images.length >= maxImages;
  const remainingSlots = maxImages - images.length;

  return (
    <div
      className={`fmp-image-uploader ${enableCameraCapture ? 'fmp-image-uploader--capture' : ''} ${className}`}
    >
      <input
        ref={pickerInputRef}
        type="file"
        // Keep the native picker as a sibling of the role=button drop zone:
        // nesting interactive controls is invalid even when the input is hidden.
        // Extensions accompany MIME types because some platforms do not map
        // HEIC/HEIF to a known MIME; the Worker remains the authoritative gate.
        accept={ACCEPTED_IMAGE_TYPES}
        multiple
        onChange={handleInputChange}
        disabled={disabled || isAtLimit}
        className="fmp-file-input"
        data-fmp-file-source="picker"
        aria-hidden="true"
        tabIndex={-1}
      />

      {enableCameraCapture ? (
        <>
          <input
            ref={cameraInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES}
            capture="environment"
            onChange={handleInputChange}
            disabled={disabled || isAtLimit}
            className="fmp-file-input"
            data-fmp-file-source="camera"
            aria-hidden="true"
            tabIndex={-1}
          />
          <div
            ref={uploadActionsRef}
            className="fmp-upload-actions"
            aria-label="Photo options"
            role="group"
            tabIndex={-1}
          >
            <button
              ref={cameraButtonRef}
              type="button"
              className="fmp-upload-action fmp-upload-action--camera"
              onClick={handleCameraClick}
              disabled={disabled || isAtLimit}
            >
              Take a photo
            </button>
            <button
              ref={pickerButtonRef}
              type="button"
              className="fmp-upload-action fmp-upload-action--picker"
              onClick={handlePickerButtonClick}
              disabled={disabled || isAtLimit}
            >
              Choose photos
            </button>
          </div>
        </>
      ) : null}

      {/* Drop Zone */}
      <div
        ref={dropzoneRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`Upload images. ${remainingSlots} slots remaining. Press Enter or click to select files.`}
        aria-disabled={disabled || isAtLimit}
        className={`fmp-dropzone ${isDragActive ? 'fmp-dropzone--active' : ''} ${disabled || isAtLimit ? 'fmp-dropzone--disabled' : ''}`}
        onClick={handleDropzoneClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="fmp-dropzone__content">
          {/* Upload Icon */}
          <svg
            className="fmp-dropzone__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>

          {/* Instructions */}
          {isAtLimit ? (
            <p className="fmp-dropzone__text">Maximum {maxImages} images reached</p>
          ) : (
            <>
              <p className="fmp-dropzone__text">
                {isDragActive
                  ? 'Drop images here'
                  : 'Drag and drop images here, or click to select'}
              </p>
              <p className="fmp-dropzone__hint">
                {remainingSlots} of {maxImages} remaining • Max {FILE_VALIDATION.MAX_SIZE_MB}MB each
                • JPG, PNG, WebP, HEIC, HEIF
              </p>
            </>
          )}
        </div>
      </div>

      {/* Validation Errors */}
      {errors.length > 0 && (
        <div role="alert" aria-live="polite" className="fmp-errors">
          <div className="fmp-errors__header">
            <span className="fmp-errors__title">Upload errors:</span>
            <button
              type="button"
              onClick={handleDismissErrors}
              className="fmp-errors__dismiss"
              aria-label="Dismiss errors"
            >
              ×
            </button>
          </div>
          <ul className="fmp-errors__list">
            {errors.map((error, index) => (
              <li key={`${error.type}-${error.fileName || index}`} className="fmp-errors__item">
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="fmp-previews" role="list" aria-label="Selected images">
          {images.map((file, index) => (
            <div
              key={`${file.name}-${file.lastModified}-${index}`}
              className="fmp-preview"
              role="listitem"
            >
              <img
                src={previews.get(file) || ''}
                alt={`Preview of ${file.name}`}
                className="fmp-preview__image"
              />
              <button
                ref={(node) => {
                  previewRemoveRefs.current[index] = node;
                }}
                type="button"
                onClick={() => handleRemove(file, index)}
                disabled={disabled}
                className="fmp-preview__remove"
                aria-label={`Remove ${file.name}`}
              >
                ×
              </button>
              <span className="fmp-preview__name" title={file.name}>
                {file.name.length > 15 ? `${file.name.slice(0, 12)}...` : file.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const imageUploaderStyles = `
  :where(.fmp-widget-scope) .fmp-image-uploader {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-md);
    width: 100%;
  }

  :where(.fmp-widget-scope) .fmp-dropzone {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: var(--fmp-size-120);
    padding: var(--fmp-space-xl);
    border: var(--fmp-border-width-strong) dashed var(--fmp-color-border-light);
    border-radius: var(--fmp-radius-control);
    background-color: var(--fmp-color-surface-muted);
    cursor: pointer;
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard), box-shadow var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-dropzone:hover:not(.fmp-dropzone--disabled) {
    border-color: var(--fmp-color-primary);
    background-color: var(--fmp-color-surface-hover);
  }

  :where(.fmp-widget-scope) .fmp-dropzone:focus-visible:not(.fmp-dropzone--disabled) {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
    border-color: var(--fmp-color-primary);
    box-shadow: var(--fmp-shadow-focus);
  }

  :where(.fmp-widget-scope) .fmp-dropzone--active {
    border-color: var(--fmp-color-primary);
    background-color: var(--fmp-color-info-bg);
  }

  :where(.fmp-widget-scope) .fmp-dropzone--disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: var(--fmp-color-surface-muted);
  }

  :where(.fmp-widget-scope) .fmp-dropzone__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-dropzone__icon {
    width: var(--fmp-size-32);
    height: var(--fmp-size-32);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-dropzone--active .fmp-dropzone__icon {
    color: var(--fmp-color-primary);
  }

  :where(.fmp-widget-scope) .fmp-dropzone__text {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-dropzone__hint {
    margin: 0;
    font-size: var(--fmp-font-size-xs);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-file-input {
    display: none;
  }

  :where(.fmp-widget-scope) .fmp-upload-actions {
    display: grid;
    gap: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-upload-actions:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-upload-action {
    min-height: var(--fmp-size-44);
    padding: var(--fmp-space-md) var(--fmp-space-lg);
    border: var(--fmp-border-width) solid var(--fmp-color-primary);
    border-radius: var(--fmp-radius-control);
    font: inherit;
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    cursor: pointer;
  }

  :where(.fmp-widget-scope) .fmp-upload-action--camera {
    color: var(--fmp-color-text-on-primary);
    background-color: var(--fmp-color-primary);
  }

  :where(.fmp-widget-scope) .fmp-upload-action--camera:hover:not(:disabled) {
    background-color: var(--fmp-color-primary-hover);
  }

  :where(.fmp-widget-scope) .fmp-upload-action--picker {
    color: var(--fmp-color-primary);
    background-color: var(--fmp-color-surface);
  }

  :where(.fmp-widget-scope) .fmp-upload-action--picker:hover:not(:disabled) {
    background-color: var(--fmp-color-surface-hover);
  }

  :where(.fmp-widget-scope) .fmp-upload-action:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
    box-shadow: var(--fmp-shadow-focus);
  }

  :where(.fmp-widget-scope) .fmp-upload-action:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  :where(.fmp-widget-scope) .fmp-image-uploader--capture .fmp-preview__remove,
  :where(.fmp-widget-scope) .fmp-image-uploader--capture .fmp-errors__dismiss {
    width: var(--fmp-size-44);
    height: var(--fmp-size-44);
  }

  :where(.fmp-widget-scope) .fmp-image-uploader--capture .fmp-dropzone__text,
  :where(.fmp-widget-scope) .fmp-image-uploader--capture .fmp-dropzone__hint {
    font-size: var(--fmp-font-size-base);
  }

  @container fmp-ask (width < ${BREAKPOINTS.container.compact}) {
    :where(.fmp-widget-scope) .fmp-image-uploader--capture .fmp-dropzone {
      display: none;
    }
  }

  @container fmp-ask (min-width: ${BREAKPOINTS.container.compact}) {
    :where(.fmp-widget-scope) .fmp-image-uploader--capture .fmp-upload-actions {
      grid-template-columns: 1fr 1fr;
    }

  }

  :where(.fmp-widget-scope) .fmp-errors {
    padding: var(--fmp-space-md);
    background-color: var(--fmp-color-error-bg);
    border: var(--fmp-border-width) solid var(--fmp-color-error-border);
    border-radius: var(--fmp-radius-control);
  }

  :where(.fmp-widget-scope) .fmp-errors__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-errors__title {
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-error);
    font-size: var(--fmp-font-size-base);
  }

  :where(.fmp-widget-scope) .fmp-errors__dismiss {
    background: none;
    border: none;
    font-size: var(--fmp-font-size-lg);
    cursor: pointer;
    color: var(--fmp-color-text-muted); /* WCAG AA compliant (5.04:1 contrast) */
    padding: 0 var(--fmp-space-xs);
    line-height: var(--fmp-line-height-icon);
  }

  :where(.fmp-widget-scope) .fmp-errors__dismiss:hover {
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-errors__list {
    margin: 0;
    padding-left: var(--fmp-space-xl);
  }

  :where(.fmp-widget-scope) .fmp-errors__item {
    font-size: var(--fmp-font-size-sm);
    color: var(--fmp-color-error);
    margin-bottom: var(--fmp-space-xs);
    overflow-wrap: anywhere;
  }

  :where(.fmp-widget-scope) .fmp-errors__item:last-child {
    margin-bottom: 0;
  }

  :where(.fmp-widget-scope) .fmp-previews {
    display: flex;
    flex-wrap: wrap;
    gap: var(--fmp-space-md);
  }

  :where(.fmp-widget-scope) .fmp-preview {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: var(--fmp-size-80);
  }

  :where(.fmp-widget-scope) .fmp-preview__image {
    width: var(--fmp-size-80);
    height: var(--fmp-size-80);
    object-fit: cover;
    border-radius: var(--fmp-radius-control);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
  }

  :where(.fmp-widget-scope) .fmp-preview__remove {
    position: absolute;
    top: calc(-1 * var(--fmp-size-8));
    right: calc(-1 * var(--fmp-size-8));
    width: var(--fmp-size-24);
    height: var(--fmp-size-24);
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: var(--fmp-color-error);
    color: var(--fmp-color-text-on-primary);
    font-size: var(--fmp-font-size-base);
    line-height: var(--fmp-line-height-icon);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-preview__remove:hover:not(:disabled) {
    background-color: var(--fmp-color-error-dark);
  }

  :where(.fmp-widget-scope) .fmp-preview__remove:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  :where(.fmp-widget-scope) .fmp-preview__name {
    margin-top: var(--fmp-space-xs);
    font-size: var(--fmp-font-size-xs);
    color: var(--fmp-color-text-muted);
    text-align: center;
    word-break: break-all;
    max-width: var(--fmp-size-80);
  }

  /* ==========================================================================
   * P1-26: MOBILE RESPONSIVE STYLES
   * ==========================================================================
   * Breakpoints:
   * - Mobile: < 480px
   * - Tablet: 480px - 768px
   * - Desktop: > 768px
   * ========================================================================== */

  /* Mobile Styles (< 480px) */
  @media screen and (max-width: ${BREAKPOINTS.viewport.smallMax}) {
    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-dropzone {
      min-height: var(--fmp-size-100);
      padding: var(--fmp-space-lg);
    }

    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-dropzone__icon {
      width: var(--fmp-size-28);
      height: var(--fmp-size-28);
    }

    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-dropzone__text {
      font-size: var(--fmp-font-size-sm);
    }

    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-dropzone__hint {
      font-size: var(--fmp-font-size-xs);
    }

    /* P1-26: Increase preview gap to accommodate 44x44px touch targets (Review Fix) */
    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-previews {
      gap: var(--fmp-space-lg);
    }

    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-preview {
      width: var(--fmp-size-72);
    }

    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-preview__image {
      width: var(--fmp-size-72);
      height: var(--fmp-size-72);
    }

    /* P1-26: Ensure remove button is at least 44x44px touch target */
    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-preview__remove {
      width: var(--fmp-size-44);
      height: var(--fmp-size-44);
      top: calc(-1 * var(--fmp-size-12));
      right: calc(-1 * var(--fmp-size-12));
      font-size: var(--fmp-font-size-lg);
    }

    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-preview__name {
      max-width: var(--fmp-size-72);
      font-size: var(--fmp-font-size-xs);
    }

    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-errors {
      padding: var(--fmp-size-10);
    }

    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-errors__title {
      font-size: var(--fmp-font-size-sm);
    }

    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-errors__item {
      font-size: var(--fmp-font-size-xs);
    }

    /* P1-26: Ensure dismiss button is at least 44x44px touch target */
    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-errors__dismiss {
      width: var(--fmp-size-44);
      height: var(--fmp-size-44);
      font-size: var(--fmp-font-size-xl);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  /* Tablet Styles (480px - 768px) */
  @media screen and (min-width: ${BREAKPOINTS.viewport.mediumMin}) and (max-width: ${BREAKPOINTS.viewport.mediumMax}) {
    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-dropzone {
      min-height: var(--fmp-size-110);
    }

    /* P1-26: Slightly larger touch targets for tablet */
    :where(.fmp-widget-scope) .fmp-image-uploader:not(.fmp-image-uploader--capture) .fmp-preview__remove {
      width: var(--fmp-size-32);
      height: var(--fmp-size-32);
      font-size: var(--fmp-font-size-lg);
    }
  }

  /* High contrast mode support for accessibility */
  @media (prefers-contrast: high) {
    :where(.fmp-widget-scope) .fmp-dropzone:focus-visible:not(.fmp-dropzone--disabled) {
      outline: var(--fmp-outline-width) var(--fmp-outline-style) currentColor;
      outline-offset: var(--fmp-outline-offset);
      box-shadow: none;
    }

    :where(.fmp-widget-scope) .fmp-upload-action:focus-visible {
      outline-color: currentColor;
      box-shadow: none;
    }

    :where(.fmp-widget-scope) .fmp-upload-actions:focus-visible {
      outline-color: currentColor;
    }

    :where(.fmp-widget-scope) .fmp-preview__remove:focus-visible {
      outline: var(--fmp-outline-width) var(--fmp-outline-style) currentColor;
      outline-offset: var(--fmp-outline-offset);
    }

    :where(.fmp-widget-scope) .fmp-errors {
      border-width: var(--fmp-border-width-strong);
    }
  }

  /* P1-26 Review Fix: Reduced motion support for accessibility consistency */
  @media (prefers-reduced-motion: reduce) {
    :where(.fmp-widget-scope) .fmp-dropzone,
    :where(.fmp-widget-scope) .fmp-preview__remove {
      transition: none;
    }
  }
`;
